import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * Coral 立宪 — Modal (spec 2.5)
 *
 * 复合组件: Modal + Modal.Header + Modal.Body + Modal.Footer.
 * Shell 自带 surface-raised + 圆角 + 关闭按钮.
 *
 * - createPortal 挂 document.body
 * - ESC 键关闭
 * - backdrop 点击关闭: **默认 false** (closeOnBackdrop)。编辑类 modal 辛苦填半天,
 *   误触遮罩就全丢 — 触屏 POS 上点外部关闭是反模式, 统一默认不关, 必须显式点
 *   X / 取消 / 保存。个别确实想要点外部关的(如非阻断系统提示)传 closeOnBackdrop={true}。
 * - 当 closeOnBackdrop=true 时用 onPointerDown 而非 onMouseDown 关闭:
 *   触屏上一次 tap 的事件序列是 touch/pointer 全部先发生(useLongPress 在 touchend
 *   打开 modal), 浏览器**之后**才合成 mousedown/mouseup/click 这串 compat mouse 事件,
 *   它们会落在刚渲染出来的全屏 backdrop 上。若 backdrop 监听 mousedown(或 click),
 *   打开 modal 的那次 tap 的合成残留就会立即把 modal 关掉(仅 Windows 触屏复现,
 *   dev 鼠标测不出)。pointer 事件不会被 compat-mouse 合成机制重放, 用 onPointerDown
 *   即从根上隔离这串残留事件。
 * - role="dialog" aria-modal="true" + aria-labelledby 指向 Header 标题
 * - 打开时聚焦首个可聚焦元素, Tab / Shift+Tab 在 dialog 内循环, 关闭时把焦点还给
 *   打开它的那个元素 (自实现, 无 focus-trap-react 依赖, plan 明确禁引)。
 *   `aria-modal="true"` 只是**声明**排他, 浏览器并不据此拦截 Tab —— 没有 trap 时
 *   键盘焦点会走到 modal 背后那一整页控件上, 而屏幕阅读器仍然认为它们不可达。
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';

/**
 * `center` — 桌面 / 大屏的默认形态。
 * `bottom-sheet` — 贴底,顶部两角圆角。平板竖握时屏幕上半部分拇指够不到,
 *   居中 dialog 的确认键会落在够不着的位置;crab-pad 是纯触屏 iPad,它的
 *   编辑类 modal 用这个形态。变体留在本包而不是让消费端自建第二套 dialog ——
 *   自建就意味着 backdrop 行为 / focus trap / ESC 三条保证各写一遍。
 */
export type ModalPlacement = 'center' | 'bottom-sheet';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  placement?: ModalPlacement;
  closeOnBackdrop?: boolean;
  children: React.ReactNode;
}

export interface ModalHeaderProps {
  title: string;
  /**
   * 关闭按钮的可访问名称,**必传**。visual 包不持有 locale,内置英文 "Close" 会把
   * 一个不可翻译的可访问名称发给每个消费端 —— 西班牙语门店的屏幕阅读器读到英文。
   * 调用方传本地化文案(red_coral: `t('common.close')`)。
   */
  closeLabel: string;
  subtitle?: string;
  actions?: React.ReactNode;
  onClose?: () => void;
  /** Keep the close affordance visible but inert while the owner is processing. */
  closeDisabled?: boolean;
}

export interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  scroll?: boolean;
}

export interface ModalFooterProps {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'between';
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-[95vw]',
};

interface ModalContextValue {
  onClose: () => void;
  titleId: string;
  registerTitle: (present: boolean) => void;
}

const ModalContext = React.createContext<ModalContextValue | null>(null);

/**
 * `:not([disabled])` 排除禁用控件 —— 它们不在 Tab 序列里, 当成 trap 的边界会让
 * 循环停在一个收不到焦点的元素上。
 */
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableWithin(node: HTMLElement): HTMLElement[] {
  return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * 打开中的 modal 栈,栈顶是最上层的那个。
 *
 * ESC 与 Tab 都监听 window,所以叠加时每个 modal 都会收到同一个事件。不分层的话
 * 一次 ESC 会把整摞一起关掉,两个 focus trap 还会互相把焦点抢回自己那层。叠加是
 * 真实形态:crab-pad 的订单详情里,单项操作 sheet 开着的时候点「赠送」会再开一个
 * 填原因的 modal,前者并不关闭 —— 一次 ESC 连填了一半的原因一起丢。
 *
 * 用模块级数组而不是 context:两层 modal 之间隔着 `createPortal`,它们在 React 树上
 * 是兄弟而不是祖孙,context 传不过去。
 */
const OPEN_MODALS: symbol[] = [];

const PLACEMENT_BACKDROP_CLASS: Record<ModalPlacement, string> = {
  center: 'items-center justify-center p-4',
  // 贴底不留下边距, sheet 自己用 safe-area padding 处理 Home indicator。
  'bottom-sheet': 'items-end justify-center',
};

const PLACEMENT_DIALOG_CLASS: Record<ModalPlacement, string> = {
  center: 'rounded-2xl max-h-[90vh]',
  // `coral-sheet-enter` 定义在本包的 base.css —— 它必须作用在 dialog 容器上,
  // 加到 children 上只会让内容在已就位的框内滑动(见 base.css 的注释)。
  'bottom-sheet': 'rounded-t-3xl max-h-[85vh] coral-sheet-enter',
};

function ModalRoot({
  isOpen,
  onClose,
  size = 'md',
  placement = 'center',
  closeOnBackdrop = false,
  children,
}: ModalProps) {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const [hasTitle, setHasTitle] = React.useState(false);

  const instanceId = React.useRef<symbol | null>(null);
  if (instanceId.current === null) instanceId.current = Symbol('coral-modal');

  // 入栈/出栈。effect 按渲染顺序执行,所以后渲染的(更上层的)那个后入栈、在栈顶。
  React.useEffect(() => {
    if (!isOpen) return;
    const id = instanceId.current!;
    OPEN_MODALS.push(id);
    return () => {
      const at = OPEN_MODALS.lastIndexOf(id);
      if (at !== -1) OPEN_MODALS.splice(at, 1);
    };
  }, [isOpen]);

  const isTopmost = React.useCallback(
    () => OPEN_MODALS[OPEN_MODALS.length - 1] === instanceId.current,
    [],
  );

  const registerTitle = React.useCallback((present: boolean) => {
    setHasTitle(present);
  }, []);

  const contextValue = React.useMemo<ModalContextValue>(
    () => ({ onClose, titleId, registerTitle }),
    [onClose, titleId, registerTitle],
  );

  // ESC to close + Tab / Shift+Tab focus trap
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      // 只有最上层的 modal 处理键盘 —— 下面几层此刻是背景。
      if (!isTopmost()) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const node = dialogRef.current;
      if (!node) return;
      const focusable = focusableWithin(node);
      if (focusable.length === 0) {
        // 无可聚焦内容: 焦点无处可去, 但也不能放它跑到背景页面上。
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const escaped = !(active instanceof Node) || !node.contains(active);

      if (e.shiftKey) {
        if (escaped || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (escaped || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, isTopmost]);

  // 打开时聚焦首个可聚焦元素, 关闭/卸载时把焦点还给打开它的那个元素。
  // 不还焦点的话, 关闭后焦点落在 <body> 上, 键盘用户要从页首重新 Tab 一遍。
  React.useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement;
    const node = dialogRef.current;
    if (node) focusableWithin(node)[0]?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdrop = (e: React.PointerEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  const node = (
    <ModalContext.Provider value={contextValue}>
      <div
        className={
          'fixed inset-0 z-50 flex bg-surface-overlay ' + PLACEMENT_BACKDROP_CLASS[placement]
        }
        onPointerDown={handleBackdrop}
        data-testid="coral-modal-backdrop"
        data-placement={placement}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={hasTitle ? titleId : undefined}
          className={
            'relative w-full ' +
            SIZE_CLASS[size] +
            ' bg-surface-raised shadow-xl overflow-hidden flex flex-col ' +
            PLACEMENT_DIALOG_CLASS[placement]
          }
          data-size={size}
          data-placement={placement}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  );

  return createPortal(node, document.body);
}

const Header: React.FC<ModalHeaderProps> = ({
  title,
  closeLabel,
  subtitle,
  actions,
  onClose,
  closeDisabled = false,
}) => {
  const ctx = React.useContext(ModalContext);
  const close = onClose ?? ctx?.onClose;
  const registerTitle = ctx?.registerTitle;

  React.useEffect(() => {
    if (!registerTitle) return;
    registerTitle(true);
    return () => registerTitle(false);
  }, [registerTitle]);

  return (
    <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-edge-subtle">
      <div className="flex-1 min-w-0">
        <h2 id={ctx?.titleId} className="text-lg font-semibold text-ink-primary truncate">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-sm text-ink-secondary truncate">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
      {close && (
        <button
          type="button"
          onClick={close}
          disabled={closeDisabled}
          aria-label={closeLabel}
          className={
            // 关闭键也是触摸目标: 旧的 h-8(32px) 低于本组件所属 spec 自己立的
            // 48px 地板 —— 触屏上最容易误点成背后的内容。
            'flex h-[var(--size-touch-rec)] w-[var(--size-touch-rec)] shrink-0 ' +
            'items-center justify-center rounded-lg ' +
            'text-ink-secondary hover:bg-surface-muted hover:text-ink-primary ' +
            'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-edge-focus ' +
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-ink-secondary'
          }
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};

const Body: React.FC<ModalBodyProps> = ({ children, className, scroll = true }) => {
  const classes = [
    'px-6 py-4 flex-1',
    scroll ? 'overflow-y-auto' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <div className={classes}>{children}</div>;
};

const ALIGN_CLASS: Record<NonNullable<ModalFooterProps['align']>, string> = {
  left: 'justify-start',
  right: 'justify-end',
  between: 'justify-between',
};

const Footer: React.FC<ModalFooterProps> = ({ children, align = 'right' }) => {
  return (
    <div
      className={
        'flex items-center gap-2 px-6 py-4 border-t border-edge-subtle bg-surface-base ' +
        ALIGN_CLASS[align]
      }
    >
      {children}
    </div>
  );
};

type ModalCompound = React.FC<ModalProps> & {
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
};

export const Modal = ModalRoot as ModalCompound;
Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
